import Immutable, { OrderedMap } from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { NewSubmittal } from 'records/NewSubmittal';
import {
  SelectedApprovedFiles,
  SelectedSubmittals,
  Submittal,
  SubmittalId,
  SubmittalListTypes,
} from 'records/Submittal';
import {
  createSubmittal,
  deleteSubmittal,
  designSubmittalResponse,
  getSubmittalCategories,
  getSubmittalReviewerResponses,
  getSubmittals,
  getSubmittalStatuses,
  respondToSubmittal,
  updateSubmittal,
  updateSubmittalNoFile,
  uploadSubmittalDocument,
  uploadSubmittalLog,
} from 'services/submittalService';

export interface SubmittalActions {
  setSelectedSubmittals: (
    actionParams: AppStateActionParams,
    selectedSubmittals: SelectedSubmittals
  ) => AppState;

  getSubmittalStatusesAndCategories: (
    actionParams: AppStateActionParams
  ) => Promise<AppState>;

  getSubmittals: (
    actionParams: AppStateActionParams,
    listType: SubmittalListTypes
  ) => Promise<AppState>;

  createSubmittal: (
    actionParams: AppStateActionParams,
    newSubmittal: NewSubmittal,
    files: Immutable.List<File>
  ) => Promise<AppState>;

  updateSubmittal: (
    actionParams: AppStateActionParams,
    updatedSubmittal: Submittal,
    files: Immutable.List<File>
  ) => Promise<AppState>;

  updateSubmittals: (
    actionParams: AppStateActionParams,
    updatedSubmittals: Submittal[]
  ) => Promise<AppState>;

  respondToSubmittal: (
    actionParams: AppStateActionParams,
    updatedSubmittal: Submittal,
    files: Immutable.List<File>
  ) => Promise<AppState>;

  reviewSubmittal: (
    actionParams: AppStateActionParams,
    updatedSubmittal: Submittal,
    files: Immutable.List<File>
  ) => Promise<AppState>;

  deleteSubmittal: (
    actionParams: AppStateActionParams,
    submittal: Submittal
  ) => Promise<AppState>;

  deleteSubmittalsBulk: (
    actionParams: AppStateActionParams,
    submittals: OrderedMap<string, Submittal>
  ) => Promise<AppState>;

  uploadSubmittalLog: (
    actionParams: AppStateActionParams,
    log: File
  ) => Promise<AppState>;

  setSelectedApprovedFiles: (
    actionParams: AppStateActionParams,
    selectedFiles: SelectedApprovedFiles
  ) => AppState;

  addSelectedApprovedFile: (
    actionParams: AppStateActionParams,
    approvedFileId: string
  ) => AppState;

  deleteSelectedApprovedFile: (
    actionParams: AppStateActionParams,
    approvedFileId: string
  ) => AppState;
}

const submittalActions: SubmittalActions = {
  setSelectedSubmittals: (
    { state }: AppStateActionParams,
    selectedSubmittals: SelectedSubmittals
  ): AppState => {
    return state().set('selectedSubmittals', selectedSubmittals);
  },

  getSubmittalStatusesAndCategories: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const [submittalStatuses, submittalCategories, submittalReviewerResponses] =
      await Promise.all([
        getSubmittalStatuses(),
        getSubmittalCategories(),
        getSubmittalReviewerResponses(),
      ]);

    return state()
      .set('submittalStatuses', submittalStatuses)
      .set('submittalCategories', submittalCategories)
      .set('submittalReviewerResponses', submittalReviewerResponses);
  },

  getSubmittals: async (
    { state }: AppStateActionParams,
    listType: SubmittalListTypes
  ): Promise<AppState> => {
    const { selectedProject } = state();

    const submittals = await getSubmittals(selectedProject, listType);

    const summittalsMap = submittals.reduce(
      (acc, s) => acc.set(s.id, s),
      Immutable.OrderedMap<SubmittalId, Submittal>()
    );

    return state().set('submittals', summittalsMap);
  },

  createSubmittal: async (
    { state }: AppStateActionParams,
    newSubmittal: NewSubmittal,
    files: Immutable.List<File>
  ): Promise<AppState> => {
    let createdSubmittal = await createSubmittal(newSubmittal);

    if (files.count() > 0) {
      const subResp = await uploadSubmittalDocument(
        createdSubmittal.projectid,
        files.first(),
        createdSubmittal.id
      );
      createdSubmittal = subResp || createdSubmittal;
    }

    return state().setIn(['submittals', createdSubmittal.id], createdSubmittal);
  },

  updateSubmittal: async (
    { state }: AppStateActionParams,
    updatedSubmittal: Submittal,
    files: Immutable.List<File>
  ): Promise<AppState> => {
    const newSubmittal = await updateSubmittal(updatedSubmittal, files);

    return state().setIn(['submittals', newSubmittal.id], newSubmittal);
  },

  updateSubmittals: async (
    { state }: AppStateActionParams,
    updatedSubmittals: Submittal[]
  ): Promise<AppState> => {
    try {
      const updatedSubmittalsByKey = updatedSubmittals.reduce(
        (acc: SubmittalMap, sub: Submittal): SubmittalMap =>
          acc.set(sub.id, sub),
        Immutable.Map<string, Submittal>()
      );

      return state().mergeIn(['submittals'], updatedSubmittalsByKey);
    } catch (err) {
      return state();
    }
  },

  respondToSubmittal: async (
    { state }: AppStateActionParams,
    updatedSubmittal: Submittal,
    files: Immutable.List<File>
  ): Promise<AppState> => {
    const newSubmittal = await respondToSubmittal(
      updatedSubmittal,
      files.first()
    );

    return state().setIn(['submittals', newSubmittal.id], newSubmittal);
  },

  reviewSubmittal: async (
    { state }: AppStateActionParams,
    updatedSubmittal: Submittal,
    files: Immutable.List<File>
  ): Promise<AppState> => {
    const newSubmittal = await designSubmittalResponse(
      updatedSubmittal,
      files.first()
    );

    return state().setIn(['submittals', newSubmittal.id], newSubmittal);
  },

  deleteSubmittal: async (
    { state }: AppStateActionParams,
    submittal: Submittal
  ): Promise<AppState> => {
    const submittalDeleted = await deleteSubmittal(submittal);

    return submittalDeleted
      ? state().deleteIn(['submittals', submittal.id])
      : state();
  },

  deleteSubmittalsBulk: async (
    { state }: AppStateActionParams,
    submittals: OrderedMap<string, Submittal>
  ): Promise<AppState> => {
    await Promise.all(
      Array.from(submittals.entries()).map((s) => deleteSubmittal(s[1]))
    );

    const newSubmittals = state().submittals.filterNot((s) =>
      submittals.has(s.id)
    );

    return state().update('submittals', () => newSubmittals);
  },

  uploadSubmittalLog: async (
    { state }: AppStateActionParams,
    log: File
  ): Promise<AppState> => {
    const { selectedProject } = state();

    await uploadSubmittalLog(selectedProject, log);

    return state();
  },

  setSelectedApprovedFiles: (
    { state }: AppStateActionParams,
    selectedFiles: SelectedApprovedFiles
  ): AppState => {
    return state().set('selectedApprovedFiles', selectedFiles);
  },

  addSelectedApprovedFile: (
    { state }: AppStateActionParams,
    approvedFileId: string
  ): AppState => {
    return state().update(
      'selectedApprovedFiles',
      (selectedApprovedFiles: SelectedApprovedFiles): SelectedApprovedFiles =>
        selectedApprovedFiles.add(approvedFileId)
    );
  },

  deleteSelectedApprovedFile: (
    { state }: AppStateActionParams,
    approvedFileId: string
  ): AppState => {
    return state().update(
      'selectedApprovedFiles',
      (selectedApprovedFiles: SelectedApprovedFiles): SelectedApprovedFiles =>
        selectedApprovedFiles.delete(approvedFileId)
    );
  },
};

export type SubmittalMap = Immutable.Map<string, Submittal>;

export default submittalActions;
